import { useRef, useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { History, Loader2, Play, Download } from "lucide-react";
import GIF from "gif.js";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtEpoch(key) {
  return key ? key.replace(/^D_(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3") : key;
}

function daysBetweenEpochs(a, b) {
  const parse = k => { const m = k?.match(/^D_(\d{4})(\d{2})(\d{2})$/); return m ? new Date(+m[1], +m[2]-1, +m[3]) : null; };
  const da = parse(a), db = parse(b);
  return (da && db) ? (db - da) / 86400000 : 0;
}

function fmtDelta(days) {
  if (days <= 0) return null;
  const years  = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  let label = "";
  if (years > 0)  label += `${years}y `;
  if (months > 0) label += `${months}m `;
  label = label.trim() || "<1m";
  return `${label} (${days}d)`;
}

function niceBound(absMax) {
  if (absMax === 0) return 10;
  const steps = [5, 10, 25, 50, 100, 250, 500, 1000];
  for (const s of steps) { if (absMax <= s) return s; }
  return Math.ceil(absMax / 100) * 100;
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  text:   "#1e1d3a",
  muted:  "#6b6a8f",
  subtle: "#9898b8",
  bg:     "#f6f6fb",
  border: "#e3e2f0",
  rSm:    6,
  locked: "#232231",
  accent: "#232231",
};

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children, compact }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.07em",
      textTransform: "uppercase", color: T.muted,
      marginBottom: compact ? 6 : 8,
      marginTop:    compact ? 0 : 16,
    }}>
      {children}
    </div>
  );
}

// ── Section block with top divider ────────────────────────────────────────────
function SectionBlock({ children }) {
  return (
    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, marginTop: 14 }}>
      {children}
    </div>
  );
}

// ── Axis range controls ───────────────────────────────────────────────────────
function DualRangeSlider({ label, unit = "", trackMin, trackMax, valueMin, valueMax, onChange, fmt }) {
  const trackRef = useRef(null);
  const dragRef  = useRef(null);
  const span = (trackMax - trackMin) || 1;
  const curMin = (valueMin !== "" && valueMin !== undefined) ? Number(valueMin) : trackMin;
  const curMax = (valueMax !== "" && valueMax !== undefined) ? Number(valueMax) : trackMax;
  const pct = v => Math.max(0, Math.min(100, (v - trackMin) / span * 100));
  const fmtVal = fmt || (v => Number(v).toFixed(1));

  const startDrag = (which) => (e) => {
    e.preventDefault();
    dragRef.current = which;
    const onMove = (ev) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;
      const p = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      const v = trackMin + p * span;
      if (dragRef.current === "min") onChange(Math.min(v, curMax - span * 0.004), curMax);
      else                           onChange(curMin, Math.max(v, curMin + span * 0.004));
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: T.muted }}>
        <span style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
        <span>{fmtVal(curMin)}{unit} – {fmtVal(curMax)}{unit}</span>
      </div>
      <div ref={trackRef} style={{ position: "relative", height: 6, background: T.border, borderRadius: 3, margin: "0 6px", userSelect: "none" }}>
        <div style={{ position: "absolute", left: `${pct(curMin)}%`, right: `${100 - pct(curMax)}%`, top: 0, bottom: 0, background: T.accent, borderRadius: 3, opacity: 0.35 }} />
        {/* min dot — white fill */}
        <div onMouseDown={startDrag("min")} style={{ position: "absolute", left: `${pct(curMin)}%`, top: "50%", transform: "translate(-50%,-50%)", width: 12, height: 12, borderRadius: "50%", background: "#fff", border: `2px solid ${T.accent}`, cursor: "ew-resize", zIndex: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
        {/* max dot — filled */}
        <div onMouseDown={startDrag("max")} style={{ position: "absolute", left: `${pct(curMax)}%`, top: "50%", transform: "translate(-50%,-50%)", width: 12, height: 12, borderRadius: "50%", background: T.accent, border: `2px solid ${T.accent}`, cursor: "ew-resize", zIndex: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
      </div>
    </div>
  );
}

function AxisControls({ axesSwapped, setAxesSwapped, xMin, setXMin, xMax, setXMax, yMin, setYMin, yMax, setYMax, xTrackMin, xTrackMax, yTrackMin, yTrackMax, showRangeControls, setShowRangeControls }) {
  const hasRange = xMin || xMax || yMin || yMax;
  const lbl9 = { fontSize: 9, color: T.muted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" };

  const handleSwap = (e) => {
    setAxesSwapped(e.target.checked);
    setXMin(""); setXMax(""); setYMin(""); setYMax("");
  };

  const handleToggleRange = (e) => {
    setShowRangeControls(e.target.checked);
    if (!e.target.checked) { setXMin(""); setXMax(""); setYMin(""); setYMax(""); }
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showRangeControls ? 6 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", userSelect: "none", ...lbl9, color: axesSwapped ? T.text : T.muted }}>
            <input type="checkbox" checked={axesSwapped} onChange={handleSwap} style={{ accentColor: T.accent, cursor: "pointer" }} />
            Swap Axes
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", userSelect: "none", ...lbl9, color: showRangeControls ? T.text : T.muted }}>
            <input type="checkbox" checked={showRangeControls} onChange={handleToggleRange} style={{ accentColor: T.accent, cursor: "pointer" }} />
            Adjust Range
          </label>
        </div>
        {showRangeControls && hasRange && (
          <button onClick={() => { setXMin(""); setXMax(""); setYMin(""); setYMax(""); }}
            style={{ ...lbl9, background: "none", border: `1px solid ${T.border}`, borderRadius: 3, padding: "2px 7px", cursor: "pointer" }}>
            Reset
          </button>
        )}
      </div>
      {showRangeControls && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <DualRangeSlider
            label={axesSwapped ? "Deform. (X)" : "Distance (X)"}
            unit={axesSwapped ? " mm" : " m"}
            trackMin={xTrackMin} trackMax={xTrackMax}
            valueMin={xMin} valueMax={xMax}
            onChange={(mn, mx) => { setXMin(String(mn)); setXMax(String(mx)); }}
            fmt={v => Math.round(v).toString()}
          />
          <DualRangeSlider
            label={axesSwapped ? "Distance (Y)" : "Deform. (Y)"}
            unit={axesSwapped ? " m" : " mm"}
            trackMin={yTrackMin} trackMax={yTrackMax}
            valueMin={yMin} valueMax={yMax}
            onChange={(mn, mx) => { setYMin(String(mn)); setYMax(String(mx)); }}
            fmt={v => Number(v).toFixed(1)}
          />
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function SectionProfileChart({
  profileData,
  onHoverIndex,
  hoveredIndexFromMap,
  epochs = [],
  selectedEpoch = null,
  onEpochChange,
  epochsLoading = false,
  onLoadEpochs,
  onCloseEpochs,
  variant = "default",   // "default" = Map.js   |   "cesium" = InSpektViewer
  hideHistory = false,
}) {
  const isCesium = variant === "cesium";

  const dispPlotDivRef  = useRef(null);
  const velPlotDivRef   = useRef(null);
  const hoverSourceRef  = useRef(null);
  const isSyncingRef    = useRef(false);   // prevents Plotly.Fx.hover re-entrancy loops
  const selectedEpochRef = useRef(selectedEpoch);

  const [startPoint,       setStartPoint]       = useState(null);
  const [endPoint,         setEndPoint]         = useState(null);
  const [activeHoverIndex, setActiveHoverIndex] = useState(null);
  const [isPlaying,        setIsPlaying]        = useState(false);
  const [isExporting,      setIsExporting]      = useState(false);
  const [intervalInput,    setIntervalInput]    = useState("6");
  const [showExportPanel,  setShowExportPanel]  = useState(false);
  const [exportFilename,   setExportFilename]   = useState("section-deformation");
  const [exportTitle,      setExportTitle]      = useState("");
  const [exportSpeed,      setExportSpeed]      = useState(300);
  const [histMode,         setHistMode]         = useState("slider"); // "slider" | "diff"
  const diffMode = histMode === "diff";
  const [diffEpochA,       setDiffEpochA]       = useState(null);
  const [diffEpochB,       setDiffEpochB]       = useState(null);
  const [showDiffExportPanel, setShowDiffExportPanel] = useState(false);
  const [diffExportFilename,  setDiffExportFilename]  = useState("section-diff");
  const [diffExportTitle,     setDiffExportTitle]     = useState("");
  const [diffExportShowStats, setDiffExportShowStats] = useState(true);
  const [diffShowShading,     setDiffShowShading]     = useState(false);
  const [diffExportDpi,       setDiffExportDpi]       = useState(300);
  const [isDiffExporting,     setIsDiffExporting]     = useState(false);
  const [gifExportDpi,        setGifExportDpi]        = useState(300);
  const [axesSwapped,         setAxesSwapped]         = useState(false);
  const [showRangeControls,   setShowRangeControls]   = useState(false);
  const [xMin, setXMin] = useState("");
  const [xMax, setXMax] = useState("");
  const [yMin, setYMin] = useState("");
  const [yMax, setYMax] = useState("");

  useEffect(() => { selectedEpochRef.current = selectedEpoch; }, [selectedEpoch]);

  const effectiveIndex = activeHoverIndex;

  // ESC → stop playback
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setIsPlaying(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset diff when snapshot set changes
  useEffect(() => {
    setIsPlaying(false);
    setHistMode("slider");
    setDiffEpochA(null);
    setDiffEpochB(null);
  }, [epochs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Map-driven hover — update state + show tooltip on both charts
  useEffect(() => {
    if (hoveredIndexFromMap == null) {
      if (hoverSourceRef.current === "map") {
        setActiveHoverIndex(null);
        try { window.Plotly?.Fx.hover(dispPlotDivRef.current, []); } catch (_) {}
        try { window.Plotly?.Fx.hover(velPlotDivRef.current,  []); } catch (_) {}
      }
      return;
    }
    hoverSourceRef.current = "map";
    setActiveHoverIndex(hoveredIndexFromMap);
    const pt = [{ curveNumber: 0, pointNumber: hoveredIndexFromMap }];
    try { window.Plotly?.Fx.hover(dispPlotDivRef.current, pt); } catch (_) {}
    try { window.Plotly?.Fx.hover(velPlotDivRef.current,  pt); } catch (_) {}
  }, [hoveredIndexFromMap]);

  useEffect(() => {
    if (profileData?.length >= 2) {
      setStartPoint(profileData[0]);
      setEndPoint(profileData[profileData.length - 1]);
    } else {
      setStartPoint(null); setEndPoint(null);
    }
  }, [profileData]);

  // Reset hover when profile changes
  useEffect(() => {
    setActiveHoverIndex(null);
  }, [profileData]);

  const isBlurred = !profileData || profileData.length === 0;
  const hasEpochs = epochs.length > 1;

  // ── Data derivation ──────────────────────────────────────────────────────────
  const xVals = profileData?.map(p => +(p.distance * 1000).toFixed(0)) || [];

  const displacementValues = profileData?.map(p =>
    (selectedEpoch && p.epochs) ? (p.epochs[selectedEpoch] ?? null) : (p.displacement ?? null)
  ) || [];

  const epochIdx  = epochs.indexOf(selectedEpoch);
  const prevEpoch = epochIdx > 0 ? epochs[epochIdx - 1] : null;

  const velocityValues = profileData?.map(p => {
    if (prevEpoch && p.epochs) {
      const curr = p.epochs[selectedEpoch], prev = p.epochs[prevEpoch];
      const days = daysBetweenEpochs(prevEpoch, selectedEpoch);
      if (curr != null && prev != null && days > 0) return (curr - prev) / days * 365;
    }
    return p.velocity ?? null;
  }) || [];

  // ── Diff mode data (no IDW — values direct from epochs at profile positions) ──
  const diffEpochAIdx  = epochs.indexOf(diffEpochA);
  const diffEpochBIdx  = epochs.indexOf(diffEpochB);
  const prevDiffEpochA = diffEpochAIdx > 0 ? epochs[diffEpochAIdx - 1] : null;
  const prevDiffEpochB = diffEpochBIdx > 0 ? epochs[diffEpochBIdx - 1] : null;

  const diffDisplacementA = (diffMode && diffEpochA)
    ? (profileData?.map(p => p.epochs?.[diffEpochA] ?? null) || []) : [];
  const diffDisplacementB = (diffMode && diffEpochB)
    ? (profileData?.map(p => p.epochs?.[diffEpochB] ?? null) || []) : [];

  const diffVelocityA = (diffMode && diffEpochA && prevDiffEpochA)
    ? (profileData?.map(p => {
        if (!p.epochs) return null;
        const c = p.epochs[diffEpochA], pv = p.epochs[prevDiffEpochA];
        const d = daysBetweenEpochs(prevDiffEpochA, diffEpochA);
        return (c != null && pv != null && d > 0) ? (c - pv) / d * 365 : null;
      }) || []) : [];
  const diffVelocityB = (diffMode && diffEpochB && prevDiffEpochB)
    ? (profileData?.map(p => {
        if (!p.epochs) return null;
        const c = p.epochs[diffEpochB], pv = p.epochs[prevDiffEpochB];
        const d = daysBetweenEpochs(prevDiffEpochB, diffEpochB);
        return (c != null && pv != null && d > 0) ? (c - pv) / d * 365 : null;
      }) || []) : [];

  const diffDispBound = (() => {
    if (!diffMode) return null;
    let m = 0;
    [...diffDisplacementA, ...diffDisplacementB].forEach(v => { if (v != null) m = Math.max(m, Math.abs(v)); });
    return niceBound(m) || dispBound;
  })();
  const diffVelBound = (() => {
    if (!diffMode) return null;
    let m = 0;
    [...diffVelocityA, ...diffVelocityB].forEach(v => { if (v != null) m = Math.max(m, Math.abs(v)); });
    return niceBound(m) || velBound;
  })();

  const hasDispData = displacementValues.some(v => v != null);
  const hasVelData  = velocityValues.some(v => v != null);

  // ── Global Y bounds ──────────────────────────────────────────────────────────
  const dispAbsMax = (() => {
    if (!profileData) return 0;
    let m = 0;
    if (hasEpochs) {
      profileData.forEach(p => { if (!p.epochs) return; epochs.forEach(ep => { const v = p.epochs[ep]; if (v != null) m = Math.max(m, Math.abs(v)); }); });
    } else {
      displacementValues.forEach(v => { if (v != null) m = Math.max(m, Math.abs(v)); });
    }
    return m;
  })();

  const velAbsMax = (() => {
    if (!profileData) return 0;
    let m = 0;
    if (hasEpochs && epochs.length >= 2) {
      for (let i = 1; i < epochs.length; i++) {
        const ep = epochs[i], pr = epochs[i - 1];
        const days = daysBetweenEpochs(pr, ep);
        if (days <= 0) continue;
        profileData.forEach(p => {
          if (!p.epochs) return;
          const curr = p.epochs[ep], prev = p.epochs[pr];
          if (curr != null && prev != null) m = Math.max(m, Math.abs((curr - prev) / days * 365));
        });
      }
    } else {
      velocityValues.forEach(v => { if (v != null) m = Math.max(m, Math.abs(v)); });
    }
    return m;
  })();

  const dispBound = niceBound(dispAbsMax);
  const velBound  = niceBound(velAbsMax);

  // ── Plotly trace builder ─────────────────────────────────────────────────────
  const dotColor  = "#9898b8";
  const dotBorder = "#9898b8";

  const makeTraces = (yVals, unit) => {
    const sw = axesSwapped;
    const traces = [
      {
        type: "scatter",
        mode: "lines",
        x: sw ? yVals : xVals,
        y: sw ? xVals : yVals,
        fill: sw ? "tozerox" : "tozeroy",
        fillcolor: "rgba(35,34,49,0.06)",
        line: { color: "#232231", width: 2 },
        showlegend: false,
        connectgaps: false,
        hovertemplate: sw
          ? `%{y}m · %{x:.1f} ${unit}<extra></extra>`
          : `%{x}m · %{y:.1f} ${unit}<extra></extra>`,
      },
    ];

    if (effectiveIndex != null && yVals[effectiveIndex] != null) {
      traces.push({
        type: "scatter",
        mode: "markers",
        x: sw ? [yVals[effectiveIndex]] : [xVals[effectiveIndex]],
        y: sw ? [xVals[effectiveIndex]] : [yVals[effectiveIndex]],
        marker: { color: dotColor, size: 9, line: { color: dotBorder, width: 2 } },
        hoverinfo: "skip",
        showlegend: false,
      });
    }

    return traces;
  };


  // ── Diff trace builder: two labelled lines, optional shading ───────────────
  const makeDiffTraces = (yValsA, yValsB, unit, labelA, labelB, withShading = false) => {
    const sw = axesSwapped;
    return [
      {
        type: "scatter", mode: "lines",
        x: sw ? yValsA : xVals,
        y: sw ? xVals  : yValsA,
        line: { color: "#9898b8", width: 1.5, dash: "dot" },
        fill: "none",
        name: labelA, showlegend: true, connectgaps: false,
        hovertemplate: sw
          ? `%{y}m · %{x:.1f} ${unit}<extra>${labelA}</extra>`
          : `%{x}m · %{y:.1f} ${unit}<extra>${labelA}</extra>`,
      },
      {
        type: "scatter", mode: "lines",
        x: sw ? yValsB : xVals,
        y: sw ? xVals  : yValsB,
        line: { color: "#232231", width: 1.5 },
        fill: withShading ? (sw ? "tonextx" : "tonexty") : "none",
        fillcolor: withShading ? "rgba(35,34,49,0.07)" : undefined,
        name: labelB, showlegend: true, connectgaps: false,
        hovertemplate: sw
          ? `%{y}m · %{x:.1f} ${unit}<extra>${labelB}</extra>`
          : `%{x}m · %{y:.1f} ${unit}<extra>${labelB}</extra>`,
      },
    ];
  };

  // ── Plotly layout builder ────────────────────────────────────────────────────
  const makePlotLayout = (yAxisTitle, yBound, showXTitle, annotation, showLegend = false, swapped = false, uirev = "stable") => {
    const pXMin = xMin !== "" ? Number(xMin) : null;
    const pXMax = xMax !== "" ? Number(xMax) : null;
    const pYMin = yMin !== "" ? Number(yMin) : null;
    const pYMax = yMax !== "" ? Number(yMax) : null;

    // Deformation axis: user values override symmetric bounds; X inputs map to it when swapped
    const defRange = swapped
      ? [pXMin ?? -yBound, pXMax ?? yBound]
      : [pYMin ?? -yBound, pYMax ?? yBound];

    // Distance axis: user values if set, else Plotly autorange; Y inputs map to it when swapped
    const distUserRange = swapped ? [pYMin, pYMax] : [pXMin, pXMax];
    const distHas = distUserRange[0] !== null || distUserRange[1] !== null;

    const defAxis = {
      title: { text: yAxisTitle, font: { size: 9, color: T.muted }, standoff: 4 },
      tickfont: { size: 9, color: T.muted },
      gridcolor: T.border, linecolor: T.border,
      zeroline: true, zerolinecolor: "#c5c4e0", zerolinewidth: 1,
      range: defRange,
      fixedrange: true,
    };
    const distAxis = {
      title: { text: "Distance (m)", font: { size: 9, color: T.muted }, standoff: 4 },
      tickfont: { size: 9, color: T.muted },
      gridcolor: T.border, linecolor: T.border,
      zeroline: false,
      fixedrange: true,
      ...(distHas ? { range: distUserRange } : { autorange: true }),
    };
    return {
      autosize: true,
      height: swapped ? 260 : 160,
      margin: swapped
        ? { l: 56, r: 10, t: showLegend ? 28 : 8, b: 44 }
        : { l: 56, r: 10, t: showLegend ? 28 : 8, b: showXTitle ? 44 : 22 },
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "#ffffff",
      showlegend: showLegend,
      legend: showLegend ? { orientation: "h", x: 0, y: 1.18, font: { size: 9 }, bgcolor: "rgba(0,0,0,0)", borderwidth: 0 } : undefined,
      hovermode: "closest",
      hoverlabel: { font: { size: 10 }, namelength: 0 },
      annotations: annotation ? [annotation] : [],
      uirevision: uirev,
      xaxis: swapped ? defAxis : {
        title: showXTitle ? { text: "Distance (m)", font: { size: 9, color: T.muted } } : { text: "" },
        tickfont: { size: 9, color: T.muted },
        gridcolor: T.border, linecolor: T.border,
        zeroline: false, fixedrange: true,
        ...(distHas ? { range: distUserRange } : { autorange: true }),
      },
      yaxis: swapped ? distAxis : defAxis,
    };
  };

  const plotConfig = { displayModeBar: false, responsive: true };

  // ── Hover / click handlers ───────────────────────────────────────────────────
  // Per-chart hover: sync tooltip to the other chart via Plotly.Fx.hover.
  // isSyncingRef prevents the programmatic hover from re-entering and looping.
  const makePlotHoverHandler = (otherDivRef) => (data) => {
    const pt = data.points?.find(p => p.curveNumber === 0);
    if (pt == null) return;
    hoverSourceRef.current = "chart";
    setActiveHoverIndex(pt.pointIndex);
    onHoverIndex?.(pt.pointIndex);
    if (!isSyncingRef.current && otherDivRef.current) {
      isSyncingRef.current = true;
      try { window.Plotly?.Fx.hover(otherDivRef.current, [{ curveNumber: 0, pointNumber: pt.pointIndex }]); } catch (_) {}
      isSyncingRef.current = false;
    }
  };

  const handleDispHover = makePlotHoverHandler(velPlotDivRef);
  const handleVelHover  = makePlotHoverHandler(dispPlotDivRef);

  const handlePlotUnhover = () => {
    hoverSourceRef.current = null;
    setActiveHoverIndex(null);
    onHoverIndex?.(null);
    if (!isSyncingRef.current) {
      isSyncingRef.current = true;
      try { window.Plotly?.Fx.hover(dispPlotDivRef.current, []); } catch (_) {}
      try { window.Plotly?.Fx.hover(velPlotDivRef.current,  []); } catch (_) {}
      isSyncingRef.current = false;
    }
  };

  // ── Time slider playback ─────────────────────────────────────────────────────
  const handlePlay = () => {
    if (!hasEpochs) return;
    onEpochChange?.(epochs[0]);
    setIsPlaying(true);
  };

  useEffect(() => {
    if (!isPlaying || !hasEpochs) return;
    const id = setInterval(() => {
      const currentIdx = epochs.indexOf(selectedEpochRef.current);
      if (currentIdx >= epochs.length - 1) { setIsPlaying(false); return; }
      onEpochChange?.(epochs[currentIdx + 1]);
    }, 600);
    return () => clearInterval(id);
  }, [isPlaying, hasEpochs, epochs, onEpochChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── GIF export (Plotly.toImage to compose frames) ────────────────────────────
  const handleExport = async () => {
    if (!hasEpochs || isExporting) return;
    const Plotly   = window.Plotly;
    const dispDiv  = dispPlotDivRef.current;
    const velDiv   = velPlotDivRef.current;
    if ((!dispDiv && !velDiv) || !Plotly) return;

    setIsExporting(true);

    const scale   = gifExportDpi / 96;
    const W       = Math.round(((dispDiv || velDiv).clientWidth || 400) * scale);
    const CH      = Math.round(160 * scale);
    const nCharts = (dispDiv ? 1 : 0) + (velDiv ? 1 : 0);
    const GAP     = Math.round((nCharts > 1 ? 4 : 0) * scale);
    const TITLE_H = exportTitle ? Math.round(28 * scale) : 0;
    const LABEL_H = Math.round(20 * scale);
    const TOTAL_H = TITLE_H + LABEL_H + CH * nCharts + GAP * (nCharts - 1);

    const gif = new GIF({
      workers: 2, quality: 6, width: W, height: TOTAL_H,
      workerScript: `${window.location.origin}/gif.worker.js`,
    });

    const fname = (exportFilename || "section-deformation").replace(/\.gif$/i, "");
    gif.on("finished", blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${fname}.gif`; a.click();
      URL.revokeObjectURL(url);
      setIsExporting(false);
    });

    const delay   = Math.max(50, exportSpeed);
    const loadImg = src => new Promise(res => {
      const img = new Image(); img.onload = () => res(img); img.src = src;
    });

    for (let i = 0; i < epochs.length; i++) {
      const ep   = epochs[i];
      const prev = i > 0 ? epochs[i - 1] : null;

      const dVals = (profileData || []).map(p => p.epochs?.[ep] ?? null);
      const vVals = (profileData || []).map(p => {
        if (!prev || !p.epochs) return null;
        const c = p.epochs[ep], pv = p.epochs[prev];
        const d = daysBetweenEpochs(prev, ep);
        return (c != null && pv != null && d > 0) ? (c - pv) / d * 365 : null;
      });

      if (dispDiv) await Plotly.restyle(dispDiv, { y: [dVals] }, [0]);
      if (velDiv)  await Plotly.restyle(velDiv,  { y: [vVals] }, [0]);

      await new Promise(r => setTimeout(r, 60)); // let Plotly repaint

      const imgPromises = [];
      if (dispDiv) imgPromises.push(Plotly.toImage(dispDiv, { format: "png", width: W, height: CH }));
      if (velDiv)  imgPromises.push(Plotly.toImage(velDiv,  { format: "png", width: W, height: CH }));
      const imgUrls = await Promise.all(imgPromises);
      const imgs    = await Promise.all(imgUrls.map(loadImg));

      const offscreen = document.createElement("canvas");
      offscreen.width = W; offscreen.height = TOTAL_H;
      const ctx = offscreen.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, W, TOTAL_H);

      // Optional title — top-centre
      if (exportTitle) {
        ctx.fillStyle = T.text;
        ctx.font = `bold ${Math.round(W * 0.032)}px sans-serif`;
        ctx.textAlign = "center";
        ctx.fillText(exportTitle, W / 2, TITLE_H - Math.round(8 * scale));
        ctx.textAlign = "left";
      }

      // Epoch label — below title
      ctx.fillStyle = T.muted;
      ctx.font = `bold ${Math.round(W * 0.028)}px sans-serif`;
      ctx.fillText(fmtEpoch(ep), Math.round(10 * scale), TITLE_H + LABEL_H - Math.round(5 * scale));

      // Draw charts stacked
      let yOffset = TITLE_H + LABEL_H;
      for (const img of imgs) {
        ctx.drawImage(img, 0, yOffset);
        yOffset += CH + GAP;
      }

      gif.addFrame(ctx, { copy: true, delay });
      await new Promise(r => setTimeout(r, 0));
    }

    // Restore current epoch
    if (dispDiv) await Plotly.restyle(dispDiv, { y: [displacementValues] }, [0]);
    if (velDiv)  await Plotly.restyle(velDiv,  { y: [velocityValues]     }, [0]);
    gif.render();
  };

  // ── Diff PNG export ──────────────────────────────────────────────────────────
  const handleDiffExport = async () => {
    if (isDiffExporting || !diffEpochA || !diffEpochB) return;
    const Plotly  = window.Plotly;
    const dispDiv = dispPlotDivRef.current;
    const velDiv  = velPlotDivRef.current;
    if ((!dispDiv && !velDiv) || !Plotly) return;
    setIsDiffExporting(true);
    const scale   = diffExportDpi / 96;
    const W       = Math.round(((dispDiv || velDiv).clientWidth || 400) * scale);
    const CH      = Math.round(160 * scale);
    const nCharts = (dispDiv ? 1 : 0) + (velDiv ? 1 : 0);
    const GAP     = Math.round((nCharts > 1 ? 4 : 0) * scale);
    const TITLE_H = diffExportTitle ? Math.round(28 * scale) : 0;
    const STATS_H = diffExportShowStats ? Math.round(22 * scale) : 0;
    const TOTAL_H = TITLE_H + CH * nCharts + GAP * (nCharts - 1) + STATS_H;
    const loadImg = src => new Promise(res => { const img = new Image(); img.onload = () => res(img); img.src = src; });
    const imgPromises = [];
    if (dispDiv) imgPromises.push(Plotly.toImage(dispDiv, { format: "png", width: W, height: CH }));
    if (velDiv)  imgPromises.push(Plotly.toImage(velDiv,  { format: "png", width: W, height: CH }));
    const imgUrls = await Promise.all(imgPromises);
    const imgs    = await Promise.all(imgUrls.map(loadImg));
    const canvas  = document.createElement("canvas");
    canvas.width = W; canvas.height = TOTAL_H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, TOTAL_H);
    if (diffExportTitle) {
      ctx.fillStyle = T.text;
      ctx.font = `bold ${Math.round(W * 0.032)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(diffExportTitle, W / 2, TITLE_H - Math.round(8 * scale));
      ctx.textAlign = "left";
    }
    let yOff = TITLE_H;
    for (const img of imgs) { ctx.drawImage(img, 0, yOff); yOff += CH + GAP; }
    if (diffExportShowStats) {
      const daysVal = daysBetweenEpochs(diffEpochA, diffEpochB);
      const statsText = `A: ${fmtEpoch(diffEpochA)}  →  B: ${fmtEpoch(diffEpochB)}  ·  Δt ${fmtDelta(daysVal) || "—"}`;
      ctx.fillStyle = T.bg;
      ctx.fillRect(0, yOff, W, STATS_H);
      ctx.fillStyle = T.muted;
      ctx.font = `${Math.round(10 * scale)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(statsText, W / 2, yOff + Math.round(15 * scale));
      ctx.textAlign = "left";
    }
    canvas.toBlob(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(diffExportFilename || "section-diff").replace(/\.png$/i, "")}.png`; a.click();
      URL.revokeObjectURL(url);
      setIsDiffExporting(false);
    }, "image/png");
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  const diffActive  = diffMode && !!diffEpochA && !!diffEpochB;
  const profileKey  = profileData?.length ?? 0;
  const maxDist    = xVals.length > 0 ? xVals[xVals.length - 1] : 1000;
  const activeBnd  = (diffActive ? Math.max(diffDispBound || 0, diffVelBound || 0) : Math.max(dispBound, velBound)) * 1.5 || 50;
  const xTrackMin  = axesSwapped ? -activeBnd : 0;
  const xTrackMax  = axesSwapped ?  activeBnd : maxDist;
  const yTrackMin  = axesSwapped ? 0          : -activeBnd;
  const yTrackMax  = axesSwapped ? maxDist     :  activeBnd;

  const rangeSuffix = `${xMin}|${xMax}|${yMin}|${yMax}`;
  const dispLayout  = makePlotLayout("Deformation (mm)",  diffActive ? diffDispBound : dispBound, !hasVelData, null, diffActive, axesSwapped, `disp-${axesSwapped}-${profileKey}-${rangeSuffix}`);
  const velLayout   = makePlotLayout("Def. Rate (mm/yr)", diffActive ? diffVelBound  : velBound,  true,        null, diffActive, axesSwapped, `vel-${axesSwapped}-${profileKey}-${rangeSuffix}`);

  // Per-chart Plot props (handlers differ so cross-sync targets the other chart)
  const commonPlotProps = { config: plotConfig, style: { width: "100%" }, useResizeHandler: true, onUnhover: handlePlotUnhover };
  const dispPlotProps   = { ...commonPlotProps, onHover: handleDispHover };
  const velPlotProps    = { ...commonPlotProps, onHover: handleVelHover  };

  return (
    <div className="sidebar-trendline" style={{ position: "relative" }}>
      <div className="sidebar-trendline-title">
        <i className="fa-solid fa-chart-line"></i> Section Profile
      </div>

      <div className="sidebar-trendline-content" style={{ position: "relative", padding: "16px" }}>

        {isBlurred ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 16px", color: T.muted }}>
            <div style={{ fontSize: 22, flexShrink: 0, color: T.border }}>⟵⟶</div>
            <div style={{ fontSize: 12 }}>Click two points on the globe to draw a transect.</div>
          </div>
        ) : (
          <>
            {/* ── Endpoints ─────────────────────────────────────────────── */}
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              {[{ label: "Start", point: startPoint }, { label: "End", point: endPoint }].map(({ label, point }) => (
                <div key={label} style={{ flex: 1, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: T.subtle, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 11, color: T.text, fontFeatureSettings: '"tnum"' }}>
                    {point ? `${point.lat.toFixed(5)}° N` : "—"}
                  </div>
                  <div style={{ fontSize: 11, color: T.text, fontFeatureSettings: '"tnum"' }}>
                    {point ? `${point.lng.toFixed(5)}° E` : "—"}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Charts ────────────────────────────────────────────────── */}
            {isCesium ? (
              (hasDispData || hasVelData) && (
                <div>
                  {/* Axis controls */}
                  <AxisControls
                    axesSwapped={axesSwapped} setAxesSwapped={setAxesSwapped}
                    xMin={xMin} setXMin={setXMin} xMax={xMax} setXMax={setXMax}
                    yMin={yMin} setYMin={setYMin} yMax={yMax} setYMax={setYMax}
                    xTrackMin={xTrackMin} xTrackMax={xTrackMax}
                    yTrackMin={yTrackMin} yTrackMax={yTrackMax}
                    showRangeControls={showRangeControls} setShowRangeControls={setShowRangeControls}
                  />
                  {/* Chart container */}
                  <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                    {hasDispData && (
                      <div style={{ cursor: "crosshair" }}>
                        <Plot
                          {...dispPlotProps}
                          data={diffActive
                            ? makeDiffTraces(diffDisplacementA, diffDisplacementB, "mm", fmtEpoch(diffEpochA), fmtEpoch(diffEpochB), diffShowShading)
                            : makeTraces(displacementValues, "mm")}
                          layout={dispLayout}
                          onInitialized={(_, el) => { dispPlotDivRef.current = el; }}
                        />
                      </div>
                    )}
                    {hasVelData && (
                      <div style={{ cursor: "crosshair", borderTop: hasDispData ? `1px solid ${T.border}` : undefined }}>
                        <Plot
                          {...velPlotProps}
                          data={diffActive
                            ? makeDiffTraces(diffVelocityA, diffVelocityB, "mm/yr", fmtEpoch(diffEpochA), fmtEpoch(diffEpochB), diffShowShading)
                            : makeTraces(velocityValues, "mm/yr")}
                          layout={velLayout}
                          onInitialized={(_, el) => { velPlotDivRef.current = el; }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )
            ) : (
              <>
                <AxisControls
                  axesSwapped={axesSwapped} setAxesSwapped={setAxesSwapped}
                  xMin={xMin} setXMin={setXMin} xMax={xMax} setXMax={setXMax}
                  yMin={yMin} setYMin={setYMin} yMax={yMax} setYMax={setYMax}
                  xTrackMin={xTrackMin} xTrackMax={xTrackMax}
                  yTrackMin={yTrackMin} yTrackMax={yTrackMax}
                  showRangeControls={showRangeControls} setShowRangeControls={setShowRangeControls}
                />
                {hasDispData && (
                  <>
                    <SectionLabel>Deformation (mm)</SectionLabel>
                    <div style={{ cursor: "crosshair" }}>
                      <Plot
                        {...dispPlotProps}
                        data={makeTraces(displacementValues, "mm")}
                        layout={dispLayout}
                        onInitialized={(_, el) => { dispPlotDivRef.current = el; }}
                      />
                    </div>
                  </>
                )}
                {hasVelData && (
                  <>
                    <SectionLabel>Deformation Rate (mm/yr)</SectionLabel>
                    <div style={{ cursor: "crosshair" }}>
                      <Plot
                        {...velPlotProps}
                        data={makeTraces(velocityValues, "mm/yr")}
                        layout={velLayout}
                        onInitialized={(_, el) => { velPlotDivRef.current = el; }}
                      />
                    </div>
                  </>
                )}
                {/* non-cesium variant has no epoch support — diff mode not applicable */}
              </>
            )}

            {/* ── Historical data (cesium only, hidden in InSpekt) ───────── */}
            {isCesium && !hideHistory && (
              <SectionBlock>
                {!hasEpochs && !epochsLoading && (
                  <>
                    <SectionLabel compact>Historical Data</SectionLabel>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 10 }}>
                      <label
                        title="Temporal sampling interval across the dataset."
                        style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", cursor: "default" }}
                      >
                        Interval (in months):
                      </label>
                      <input
                        type="number" min={1} max={60} step={1}
                        value={intervalInput}
                        onChange={e => setIntervalInput(String(Math.round(Number(e.target.value))))}
                        title="Temporal sampling interval across the dataset."
                        style={{
                          width: 40, padding: "3px 6px", fontSize: 12,
                          border: `1px solid ${T.border}`, borderRadius: T.rSm,
                          color: T.text, background: T.bg, outline: "none",
                        }}
                      />
                      <button
                        onClick={() => onLoadEpochs?.(Math.round(Math.max(1, Number(intervalInput) || 6) * 30.44))}
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "5px 10px", background: T.text, color: "#fff",
                          border: "none", borderRadius: T.rSm,
                          fontSize: 11, fontWeight: 600, cursor: "pointer",
                          whiteSpace: "nowrap", transition: "background 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "#374151"}
                        onMouseLeave={e => e.currentTarget.style.background = T.text}
                      >
                        <History size={12} />
                        Load Historical Snapshots
                      </button>
                    </div>
                  </>
                )}

                {epochsLoading && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: "28px 0", textAlign: "center" }}>
                    <Loader2 size={22} color={T.muted} style={{ animation: "spin 1s linear infinite" }} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Loading historical data…</div>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Querying snapshots — this may take a moment.</div>
                    </div>
                  </div>
                )}

                {hasEpochs && !epochsLoading && (
                  <>
                    <SectionLabel compact>Historical Data</SectionLabel>
                    {/* ── Interval + Apply + Close — always visible ── */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                      <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", cursor: "default" }}>
                        Interval (months):
                      </label>
                      <input
                        type="number" min={1} max={60} step={1}
                        value={intervalInput}
                        onChange={e => setIntervalInput(String(Math.round(Number(e.target.value))))}
                        style={{ width: 40, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: T.bg, outline: "none" }}
                      />
                      <button
                        onClick={() => { onLoadEpochs?.(Math.round(Math.max(1, Number(intervalInput) || 6) * 30.44)); }}
                        style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: T.rSm, cursor: "pointer", color: T.muted, padding: "3px 7px", fontSize: 11, fontWeight: 600, transition: "color 0.15s, border-color 0.15s" }}
                        onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.borderColor = T.text; }}
                        onMouseLeave={e => { e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; }}
                      >Apply</button>
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={onCloseEpochs}
                        title="Close historical data"
                        style={{ background: "none", border: "none", cursor: "pointer", color: T.subtle, padding: "2px 4px", fontSize: 14, lineHeight: 1, borderRadius: 4, transition: "color 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.color = T.text}
                        onMouseLeave={e => e.currentTarget.style.color = T.subtle}
                      >✕</button>
                    </div>

                    {/* ── TIME SLIDER accordion ── */}
                    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10, marginTop: 10 }}>
                      <div
                        onClick={() => { if (histMode !== "slider") { setHistMode("slider"); setIsPlaying(false); } }}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: histMode !== "slider" ? "pointer" : "default", marginBottom: histMode === "slider" ? 6 : 0, userSelect: "none", padding: histMode !== "slider" ? "4px 8px" : 0, borderRadius: 4, background: histMode !== "slider" ? T.bg : "transparent", border: histMode !== "slider" ? `1px solid ${T.border}` : "none", transition: "background 0.15s, border-color 0.15s" }}
                        onMouseEnter={e => { if (histMode !== "slider") { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.borderColor = T.muted; } }}
                        onMouseLeave={e => { if (histMode !== "slider") { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = T.border; } }}
                      >
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: histMode === "slider" ? T.text : T.muted, transition: "color 0.15s" }}>
                          {histMode === "slider" ? `Time Slider — ${fmtEpoch(selectedEpoch)}` : "Time Slider"}
                        </span>
                        <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {histMode === "slider" ? (
                            <>
                              <button
                                onClick={handlePlay}
                                disabled={isPlaying}
                                title={isPlaying ? "Playing…" : "Play from first snapshot"}
                                style={{ background: "none", border: "none", padding: "1px 3px", cursor: isPlaying ? "default" : "pointer", color: isPlaying ? T.subtle : T.text, display: "flex", alignItems: "center", opacity: isPlaying ? 0.4 : 1, transition: "opacity 0.15s" }}
                              >
                                <Play size={11} />
                              </button>
                              <button
                                onClick={() => { if (!isExporting && !isPlaying) setShowExportPanel(p => !p); }}
                                disabled={isExporting || isPlaying}
                                title={isExporting ? "Exporting…" : "Export animation as GIF"}
                                style={{ background: "none", border: "none", padding: "1px 3px", cursor: (isExporting || isPlaying) ? "default" : "pointer", color: showExportPanel ? T.text : (isExporting ? T.subtle : T.muted), display: "flex", alignItems: "center", opacity: (isExporting || isPlaying) ? 0.4 : 1, transition: "opacity 0.15s, color 0.15s" }}
                              >
                                {isExporting ? <Loader2 size={11} style={{ animation: "spin 1s linear infinite" }} /> : <Download size={11} />}
                              </button>
                            </>
                          ) : (
                            <span style={{ fontSize: 14, color: T.subtle, lineHeight: 1 }}>›</span>
                          )}
                        </div>
                      </div>

                      {histMode === "slider" && (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.subtle, marginBottom: 4 }}>
                            <span>{fmtEpoch(epochs[0])}</span>
                            <span>{fmtEpoch(epochs[epochs.length - 1])}</span>
                          </div>
                          <input
                            type="range" min={0} max={epochs.length - 1} value={Math.max(0, epochIdx)}
                            onChange={e => { setIsPlaying(false); onEpochChange?.(epochs[Number(e.target.value)]); }}
                            style={{ width: "100%", cursor: "pointer", accentColor: T.text }}
                          />
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                            {epochs.length} snapshots
                            {selectedEpoch !== epochs[epochs.length - 1]
                              ? ` · Δt ${fmtDelta(Math.round(daysBetweenEpochs(selectedEpoch, epochs[epochs.length - 1])))} to latest`
                              : " · latest snapshot"}
                          </div>

                          {showExportPanel && (
                            <div style={{ marginTop: 10, padding: "10px 12px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: T.rSm, display: "flex", flexDirection: "column", gap: 8 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Filename</label>
                                <input type="text" value={exportFilename} onChange={e => setExportFilename(e.target.value)} placeholder="section-deformation"
                                  style={{ flex: 1, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: "#fff", outline: "none" }} />
                                <span style={{ fontSize: 11, color: T.subtle }}>.gif</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Title</label>
                                <input type="text" value={exportTitle} onChange={e => setExportTitle(e.target.value)} placeholder="optional title"
                                  style={{ flex: 1, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: "#fff", outline: "none" }} />
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Speed</label>
                                <input type="range" min={50} max={1000} step={50} value={exportSpeed} onChange={e => setExportSpeed(Number(e.target.value))} style={{ flex: 1, accentColor: T.text }} />
                                <span style={{ fontSize: 11, color: T.muted, minWidth: 36, textAlign: "right" }}>{exportSpeed}ms</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Quality</label>
                                <div style={{ display: "flex", flex: 1, border: `1px solid ${T.border}`, borderRadius: 4, overflow: "hidden" }}>
                                  {[96, 150, 300].map((dpi, i) => (
                                    <button key={dpi} onClick={() => setGifExportDpi(dpi)} style={{ flex: 1, padding: "3px 0", fontSize: 10, fontWeight: 600, border: "none", borderLeft: i > 0 ? `1px solid ${T.border}` : "none", background: gifExportDpi === dpi ? T.text : T.bg, color: gifExportDpi === dpi ? "#fff" : T.muted, cursor: "pointer", transition: "background 0.12s, color 0.12s" }}>
                                      {dpi === 96 ? "Screen" : `${dpi} DPI`}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <button
                                onClick={handleExport} disabled={isExporting}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "5px 10px", background: T.text, color: "#fff", border: "none", borderRadius: T.rSm, fontSize: 11, fontWeight: 600, cursor: isExporting ? "default" : "pointer", opacity: isExporting ? 0.5 : 1, transition: "background 0.15s" }}
                                onMouseEnter={e => { if (!isExporting) e.currentTarget.style.background = "#374151"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = T.text; }}
                              >
                                {isExporting ? <><Loader2 size={11} style={{ animation: "spin 1s linear infinite" }} /> Exporting…</> : <><Download size={11} /> Export GIF</>}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* ── DIFFERENCE accordion ── */}
                    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 8 }}>
                      <div
                        onClick={() => {
                          if (histMode !== "diff") {
                            setHistMode("diff");
                            setIsPlaying(false);
                            setDiffEpochA(epochs[0] || null);
                            setDiffEpochB(epochs[epochs.length - 1] || null);
                          }
                        }}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: histMode !== "diff" ? "pointer" : "default", marginBottom: histMode === "diff" ? 6 : 0, userSelect: "none", padding: histMode !== "diff" ? "4px 8px" : 0, borderRadius: 4, background: histMode !== "diff" ? T.bg : "transparent", border: histMode !== "diff" ? `1px solid ${T.border}` : "none", transition: "background 0.15s, border-color 0.15s" }}
                        onMouseEnter={e => { if (histMode !== "diff") { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.borderColor = T.muted; } }}
                        onMouseLeave={e => { if (histMode !== "diff") { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = T.border; } }}
                      >
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: histMode === "diff" ? T.text : T.muted, transition: "color 0.15s" }}>
                          Difference
                        </span>
                        <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {histMode === "diff" ? (
                            <button
                              onClick={() => { if (!isDiffExporting) setShowDiffExportPanel(p => !p); }}
                              title={isDiffExporting ? "Exporting…" : "Export as PNG"}
                              style={{ background: "none", border: "none", padding: "1px 3px", cursor: isDiffExporting ? "default" : "pointer", color: showDiffExportPanel ? T.text : T.muted, display: "flex", alignItems: "center", opacity: isDiffExporting ? 0.4 : 1, transition: "opacity 0.15s, color 0.15s" }}
                            >
                              {isDiffExporting ? <Loader2 size={11} style={{ animation: "spin 1s linear infinite" }} /> : <Download size={11} />}
                            </button>
                          ) : (
                            <span style={{ fontSize: 14, color: T.subtle, lineHeight: 1 }}>›</span>
                          )}
                        </div>
                      </div>

                      {histMode === "diff" && (
                        <>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 9, color: T.subtle, marginBottom: 2 }}>Epoch A (from)</div>
                              <select
                                value={diffEpochA || ""}
                                onChange={e => {
                                  const val = e.target.value || null;
                                  setDiffEpochA(val);
                                  if (val && diffEpochB && diffEpochB <= val) {
                                    const nextIdx = epochs.indexOf(val) + 1;
                                    setDiffEpochB(epochs[nextIdx] || null);
                                  }
                                }}
                                style={{ width: "100%", fontSize: 10, padding: "3px 4px", border: `1px solid ${T.border}`, borderRadius: 4, background: "#fff", color: T.text, outline: "none" }}
                              >
                                <option value="">— select —</option>
                                {epochs.slice(0, -1).map(ep => <option key={ep} value={ep}>{fmtEpoch(ep)}</option>)}
                              </select>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 9, color: T.subtle, marginBottom: 2 }}>Epoch B (to)</div>
                              <select
                                value={diffEpochB || ""}
                                onChange={e => setDiffEpochB(e.target.value || null)}
                                style={{ width: "100%", fontSize: 10, padding: "3px 4px", border: `1px solid ${T.border}`, borderRadius: 4, background: "#fff", color: T.text, outline: "none" }}
                              >
                                <option value="">— select —</option>
                                {epochs.filter(ep => !diffEpochA || ep > diffEpochA).map(ep => <option key={ep} value={ep}>{fmtEpoch(ep)}</option>)}
                              </select>
                            </div>
                          </div>
                          <div style={{ marginTop: 6, fontSize: 10, color: T.muted, fontStyle: "italic" }}>
                            Dotted line = A · Solid line = B
                          </div>
                          {showDiffExportPanel && (
                            <div style={{ marginTop: 10, padding: "10px 12px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: T.rSm, display: "flex", flexDirection: "column", gap: 8 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.muted, marginBottom: 2 }}>PNG Export</div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Filename</label>
                                <input type="text" value={diffExportFilename} onChange={e => setDiffExportFilename(e.target.value)} placeholder="section-diff"
                                  style={{ flex: 1, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: "#fff", outline: "none" }} />
                                <span style={{ fontSize: 11, color: T.subtle }}>.png</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Title</label>
                                <input type="text" value={diffExportTitle} onChange={e => setDiffExportTitle(e.target.value)} placeholder="optional title"
                                  style={{ flex: 1, padding: "3px 6px", fontSize: 11, border: `1px solid ${T.border}`, borderRadius: T.rSm, color: T.text, background: "#fff", outline: "none" }} />
                              </div>
                              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.muted, cursor: "pointer" }}>
                                <input type="checkbox" checked={diffExportShowStats} onChange={e => setDiffExportShowStats(e.target.checked)} />
                                Include Δ statistics footer
                              </label>
                              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.muted, cursor: "pointer" }}>
                                <input type="checkbox" checked={diffShowShading} onChange={e => setDiffShowShading(e.target.checked)} />
                                Shade area between A and B
                              </label>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", minWidth: 60 }}>Quality</label>
                                <div style={{ display: "flex", flex: 1, border: `1px solid ${T.border}`, borderRadius: 4, overflow: "hidden" }}>
                                  {[96, 150, 300].map((dpi, i) => (
                                    <button key={dpi} onClick={() => setDiffExportDpi(dpi)} style={{ flex: 1, padding: "3px 0", fontSize: 10, fontWeight: 600, border: "none", borderLeft: i > 0 ? `1px solid ${T.border}` : "none", background: diffExportDpi === dpi ? T.text : T.bg, color: diffExportDpi === dpi ? "#fff" : T.muted, cursor: "pointer", transition: "background 0.12s, color 0.12s" }}>
                                      {dpi === 96 ? "Screen" : `${dpi} DPI`}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <button
                                onClick={handleDiffExport} disabled={isDiffExporting || !diffEpochA || !diffEpochB}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "5px 10px", background: T.text, color: "#fff", border: "none", borderRadius: T.rSm, fontSize: 11, fontWeight: 600, cursor: (isDiffExporting || !diffEpochA || !diffEpochB) ? "default" : "pointer", opacity: (isDiffExporting || !diffEpochA || !diffEpochB) ? 0.5 : 1, transition: "background 0.15s" }}
                                onMouseEnter={e => { if (!isDiffExporting) e.currentTarget.style.background = "#374151"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = T.text; }}
                              >
                                {isDiffExporting ? <><Loader2 size={11} style={{ animation: "spin 1s linear infinite" }} /> Exporting…</> : <><Download size={11} /> Export PNG</>}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </SectionBlock>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SectionProfileChart;
